import React, { useEffect, useState } from 'react';
import { Card, Button, Space, Typography, Modal, Progress, Tag, Tooltip, Alert, message } from 'antd';
import { CloseOutlined, BulbOutlined, ArrowRightOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import QuestionsPanel from './QuestionsPanel';
import './VotingScreen.css'; // We'll create this file next
import { socket } from '../socket';

const { Title, Text, Paragraph } = Typography;

function VotingScreen({ 
  story, 
  estimationType, 
  onVote, 
  selectedVote, 
  onClose,
  showVotes,
  votes,
  users,
  average,
  onRevealVotes,
  votingComplete,
  onGenerateAI,
  isCreator,
  questions,
  onAddQuestion,
  onDeleteQuestion,
  onNextStory,
  hasNextStory,
  votesRevealed,
  channel
}) {
  const estimationTypes = {
    fibonacci: ['1', '2', '3', '5', '8', '13', '21'],
    tShirt: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    linear: ['1', '2', '3', '4', '5', '6', '7', '8'],
    hours: ['0.5', '1', '2', '4', '6', '8', '12', '16']
  };

  const votedCount = votes ? votes.size : 0;
  const totalCount = users ? users.length : 0;
  const progress = (votedCount / totalCount) * 100;

  const [isVoting, setIsVoting] = useState(true);

  useEffect(() => {
    socket.on('votingFinished', () => {
      setIsVoting(false);
    });

    if (votingComplete && !votesRevealed) {
      if (isCreator) {
        message.success('All votes are in! You can now reveal the votes.');
      } else {
        message.info('All votes are in! Waiting for the host to reveal votes.');
      }
    }

    return () => {
      socket.off('votingFinished');
    };
  }, [votingComplete, votesRevealed, isCreator]);

  const handleRevealVotes = () => {
    socket.emit('revealVotes', { channelId: socket.channelId });
  };

  const handleAddQuestion = (question) => {
    socket.emit('addQuestion', { 
      channelId: channel.id, 
      question 
    });
  };

  const handleDeleteQuestion = (questionId) => {
    socket.emit('deleteQuestion', { 
      channelId: channel.id, 
      questionId 
    });
  };

  return (
    <Modal
      open={true}
      onCancel={isCreator ? onClose : undefined}
      closeIcon={isCreator ? <CloseOutlined /> : null}
      footer={null}
      width="100%"
      style={{ top: 0 }}
      bodyStyle={{ 
        height: '100vh',
        padding: '24px',
        background: 'rgba(252, 252, 252, 0.95)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: 24 }}>
          {/* Main Content */}
          <div style={{ flex: 3 }}>
            {/* Voting Cards Section */}
            <Card 
              style={{ 
                marginBottom: 24,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)',
                padding: '24px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>
                  {story ? story.name : 'Quick Vote'}
                </Title>
                <Button 
                  icon={<CloseOutlined />} 
                  onClick={onClose}
                  shape="circle"
                />
              </div>

              <Progress 
                percent={progress} 
                status={votingComplete ? "success" : "active"}
                style={{ marginBottom: 24 }}
              />

              {votingComplete && (
                <Alert
                  message="Voting Complete"
                  description="Time for discussion! Review any significant differences in estimates before proceeding."
                  type="success"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <div className="voting-cards-grid">
                {estimationTypes[estimationType].map(value => (
                  <div
                    key={value}
                    className={`voting-card ${selectedVote === value ? 'selected' : ''}`}
                    onClick={() => onVote(value)}
                  >
                    <div className="voting-card-inner">
                      <span className="voting-value">{value}</span>
                    </div>
                  </div>
                ))}
              </div>

              {votingComplete && (
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  {!votesRevealed && isCreator && (
                    <Button 
                      type="primary" 
                      onClick={onRevealVotes}
                      size="large"
                    >
                      Reveal Votes
                    </Button>
                  )}
                  {votesRevealed && isCreator && (
                    <Space>
                      <Button 
                        type="default" 
                        onClick={onClose}
                        size="large"
                      >
                        Finish Voting
                      </Button>
                      {hasNextStory && (
                        <Button 
                          type="primary" 
                          onClick={onNextStory}
                          size="large"
                          icon={<ArrowRightOutlined />}
                        >
                          Next Story
                        </Button>
                      )}
                    </Space>
                  )}
                </Space>
              )}

              {votesRevealed && (
                <Card style={{ background: 'rgba(255, 255, 255, 0.5)', marginTop: 24 }}>
                  <Title level={3} style={{ textAlign: 'center', margin: '0 0 24px 0' }}>
                    Average: {average}
                  </Title>
                  <Space wrap style={{ justifyContent: 'center' }}>
                    {Array.from(votes.entries()).map(([userId, vote]) => {
                      const user = users.find(u => u.id === userId);
                      return (
                        <Card 
                          key={userId}
                          size="small"
                          style={{ 
                            width: 120,
                            margin: 8,
                            textAlign: 'center',
                            background: 'white'
                          }}
                        >
                          <Text strong>{user?.name}</Text>
                          <br />
                          <Text style={{ fontSize: 24 }}>{vote}</Text>
                        </Card>
                      );
                    })}
                  </Space>
                </Card>
              )}
            </Card>

            {/* Story Details Section */}
            <Card
              title="Story Details"
              extra={
                <Tooltip title="Generate AI Suggestions">
                  <Button 
                    icon={<BulbOutlined />} 
                    onClick={() => onGenerateAI(story)}
                    disabled={!isCreator}
                  />
                </Tooltip>
              }
              style={{ 
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)'
              }}
            >
              <div className="markdown-content">
                <ReactMarkdown>
                  {story?.description || 'No description provided'}
                </ReactMarkdown>
              </div>
            </Card>
          </div>

          {/* Questions Sidebar */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              position: 'sticky',
              top: 24,
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto'
            }}>
              <QuestionsPanel
                questions={channel.questions}
                onAddQuestion={handleAddQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                currentUser={socket.id}
                style={{ 
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(5px)'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default VotingScreen; 